import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import { rem } from '../../../utilities/style';
import { colors } from '../../../theme/colors';
import { fontWeights } from '../../../theme/type';
import { media } from '../../../theme/media';
import { richTextStyles } from '../../../styles/type';
import { recursiveHighlight } from '../../../utilities/misc';

import TextRenderer from '../../TextRenderer';

const Container = styled.details`
  border-bottom: 1px solid ${colors.gray['200']};
  break-inside: avoid;

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
      border-bottom: 1px solid ${colors.gray['700']};
  `}
`;

const Headline = styled.summary`
  list-style: none;
  display: flex;
  align-items: flex-start;
  padding: ${rem(16)} 0;
  font-size: ${rem(16)};
  font-weight: ${fontWeights.medium};
  line-height: 1.2;
  color: ${colors.blue['800']};
  cursor: pointer;

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
    color: ${colors.blue['100']};
  `}

  ${media('>tiny')} {
    padding: ${rem(24)} 0;
    font-size: ${rem(20)};
  }

  &::-webkit-details-marker {
    clip: rect(0 0 0 0);
    clip-path: inset(50%);
    width: 1px;
    height: 1px;
    overflow: hidden;
    position: absolute;
    white-space: nowrap;
  }

  &::after {
    content: '';
    display: block;
    margin-left: auto;
    border: ${rem(4)} solid transparent;
    border-top: ${rem(6)} solid ${colors.blue['800']};
    border-bottom-width: 0;
    margin-top: ${rem(10)};

    ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
      border-top-color: ${colors.blue['100']};
    `}

    ${Container}[open] & {
      transform: scaleY(-1);
      border-top-color: ${colors.blue['500']};
      ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
        border-top-color: ${colors.blue['100']};
      `}
    }
  }
`;

const Content = styled.div`
  ${richTextStyles}
  margin-bottom: ${rem(24)};
  background: ${colors.gray['50']};
  border: 1px solid ${colors.gray['200']};
  padding: ${rem(24)};
  border-radius: ${rem(10)};

  &[aria-hidden="true"] {
    visibility: hidden;
  }

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
    background-color: ${colors.blue['800']};
    border: 1px solid ${colors.blue['700']};
  `}
  mark {
    background-color: ${colors.blue['200']};
  }
`;

const regexEscape = (string) => string.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');

const AccordionItem = ({ headline, text, search }) => {
  const [opened, setOpened] = useState(false);
  const formattedText = useMemo(() => {
    if (!search || !text?.raw) {
      return text;
    }

    const regex = new RegExp(`${regexEscape(search)}(?![^{]*})`, 'gi');
    const rawJson = JSON.parse(text.raw);
    const highlightedContent = recursiveHighlight(rawJson, regex);

    return {
      ...text,
      raw: JSON.stringify(highlightedContent)
    };
  }, [search, text]);

  const handleClick = (event) => {
    event.preventDefault();
    setOpened(!opened);
  };

  useEffect(() => {
    if (search) {
      const headlineContains = headline.toLowerCase().includes(search);
      const textContains = text?.raw.toLowerCase().includes(search);

      setOpened(headlineContains || textContains);
    } else {
      setOpened(false);
    }
  }, [search, headline, text]);

  return (
    <Container
      itemScope
      itemProp="mainEntity"
      itemType="https://schema.org/Question"
      open={opened}
    >
      {headline && (
        <Headline itemProp="name" onClick={handleClick}>
          <TextRenderer text={headline} />
        </Headline>
      )}
      <Content itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer" aria-hidden={!opened}>
        {formattedText && (
          <div itemProp="text">
            <TextRenderer text={formattedText} richText />
          </div>
        )}
      </Content>
    </Container>
  );
};

AccordionItem.propTypes = {
  headline: PropTypes.string,
  text: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      raw: PropTypes.string,
      // eslint-disable-next-line react/forbid-prop-types
      references: PropTypes.arrayOf(PropTypes.object)
    })
  ]),
  search: PropTypes.string
};

export default AccordionItem;
